import * as DG from "ui/dialogs"

import React, { ReactNode } from "react"

import Button from "ui/buttons/Button"
import { css } from "@emotion/css"
import { useI18n } from "providers/I18n"

export type ModalInfo =
  | {
      type?: "primary" | "primary_destructive" | "secondary_destructive"
      title?: string
      description?: ReactNode
      cancelLabel?: string
      onSubmit?: (e?: any) => void
      submitLabel?: string
      isSubmitDisabled?: boolean
      error?: string | null
    }
  | undefined

const styles = {
  content: css`
    padding-bottom: var(--spacing-xl);
    padding-top: 0px;
  `,
  contentText: css`
    font-size: 14px;
    color: var(--dark-grey);
  `,
  errorText: css`
    font-size: 14px;
    color: var(--red);
    margin-top: var(--spacing-s);
  `,
}

type Props = ModalInfo & {
  open: boolean
  onCancel: (e?: any) => void
}

function chooseVariant(type: Props["type"]): "outlined" | "contained" {
  switch (type) {
    case "primary":
    case "primary_destructive":
      return "contained"
    case "secondary_destructive":
      return "outlined"
    default:
      return "contained"
  }
}

function chooseColor(type: Props["type"]): "primary" | "destructive" {
  switch (type) {
    case "primary":
      return "primary"
    case "primary_destructive":
      return "destructive"
    case "secondary_destructive":
      return "destructive"
    default:
      return "primary"
  }
}

export default function ConfirmationDialog(props: Props) {
  const { t } = useI18n()
  const id = "alert-dialog-title"
  const { open, onCancel, isSubmitDisabled, error, ...rest } = props

  return !!Object.keys(rest)?.length ? (
    <DG.Dialog
      open={props.open}
      onClose={props.onCancel}
      id={id}
      maxWidth="xs"
      data-testid="confirmation-modal"
    >
      <DG.DialogHeader>
        <DG.DialogTitle id={id}>{props.title}</DG.DialogTitle>
        <DG.DialogClose onClose={props.onCancel} />
      </DG.DialogHeader>

      <DG.DialogContent paddingTop className={styles.content}>
        <p className={styles.contentText}>{props.description}</p>
        {error && <p className={styles.errorText}>{error}</p>}
      </DG.DialogContent>

      <DG.DialogFooter>
        <DG.DialogActions>
          <Button variant="outlined" color="primary" onClick={props.onCancel}>
            {props.cancelLabel ?? t("confirmation.dialog.cancel")}
          </Button>
          <Button
            variant={chooseVariant(props.type)}
            color={chooseColor(props.type)}
            onClick={props?.onSubmit}
            data-testid={`delete-button-${props.type}`}
            disabled={props.isSubmitDisabled ?? false}
          >
            {props.submitLabel ?? t("confirmation.dialog.submit")}
          </Button>
        </DG.DialogActions>
      </DG.DialogFooter>
    </DG.Dialog>
  ) : null
}
